// Price-list imports (design/screens/price-import.html, data-nav="stock"; UC-IMPORTS-001..006). `/price-import` lists the imports,
// newest first, filtered by supplier and status in the address and paged, each with its counts (UC-IMPORTS-005); `/price-import/new`
// is step 1, "Supplier & file" — an active lens lab, the CSV, the list's date, where it applies and its terms (UC-IMPORTS-001); and
// `/price-import/{id}` opens one import at its step: 2 "Mapping" (UC-IMPORTS-002), 3 "Review" — the drawn screen (UC-IMPORTS-003)
// — and 4 "Apply" (UC-IMPORTS-004), with the check's and the run's progress read again every two seconds while they run, a stopped
// run's Check again or Resume, the result, a discarded import read-only, and Cancel import (UC-IMPORTS-006). Opened from Products'
// Import price list, from Import beside a lens lab's list (Settings › Markup & fitting, a supplier's record) and from the list. An
// Owner or Admin only (BR-IMPORTS-001): anyone else is told who may. No step asks for a Control PIN.
import { IMPORT_JOB_STATUSES, IMPORT_LIMITS, type ImportJobStatus, type ImportJobView, type ImportWarning, type PermissionCode, type SupplierListItem, type SupplierPriceListView } from "@visionx/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { newIdempotencyKey, type ApiResult } from "../api/client";
import { importJobs, type UploadBody } from "../api/endpoints.imports";
import { pricing } from "../api/endpoints.pricing";
import { suppliers } from "../api/endpoints.purchasing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { base64Of, bytesOf, keepFileText, refusalOf, sha256Of, textOf } from "../lib/csv";
import { DOT, formatCount, formatDate, formatFileSize, formatTime, percentFromBp } from "../lib/format";
import { bpFromPercentText, percentTextFromBp } from "../lib/money-input";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Empty, ErrorAlert, Field, Loading, Modal, Pager, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { andList } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { ImportStatusChip, checkStopped, listDatedWords, mappedSummary, outcomeWords, scopeWords, useImportAccess } from "./ImportsShared";
import { MappingStep } from "./PriceImportMapping";
import { ReviewStep } from "./PriceImportReview";
import { CheckStopped, DiscardedPanel, ImportResult, ImportStopped, RunProgress } from "./PriceImportRun";
import { LIST_KIND_WORDS, dayText, possessive, shortName, todayLocal } from "./PricingShared";
import { useActingBranch } from "./StockShared";
import { useOneStore } from "../session/one-store";

/** How often a running check or Apply is read again (UC-IMPORTS-003 step 2, UC-IMPORTS-004 step 6). */
export const POLL_MS = 2_000;
const running = (s: ImportJobStatus | undefined): boolean => s === "Checking" || s === "Importing";

/** The first of the codes a role lacks (BR-IMPORTS-001), for the no-access panel. */
function missing(s: ReturnType<typeof useSessionView>, codes: readonly PermissionCode[]): PermissionCode | undefined {
  return codes.find((c) => !can(s, c));
}

function Screen({ id, children }: { id: string; children: ReactNode }) {
  return <section className="screen" id={id}><div className="content">{children}</div></section>;
}

// --- /price-import: the history (UC-IMPORTS-005) ----------------------------------------------------------------------------

const PAGE = 25;

/** UC-IMPORTS-005 steps 1–2, A1, A4, E1: the imports newest first, with what each did; filters in the address; paged. */
export function PriceImports() {
  const s = useSessionView();
  const access = useImportAccess();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const supplierId = sp.get("supplier") ?? undefined;
  const rawStatus = sp.get("status");
  const status = (IMPORT_JOB_STATUSES as readonly string[]).includes(rawStatus ?? "") ? (rawStatus as ImportJobStatus) : undefined;
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  useEffect(() => { setCursors([]); }, [supplierId, status]);
  const readable = can(s, "imports.job.read");
  const oneStore = useOneStore();
  const labs = useQuery({ queryKey: ["suppliers", "lens-labs", "All"], queryFn: () => suppliers.list({ type: "LensLab", status: "All" }, { limit: 200 }), enabled: readable && access.read });
  const list = useQuery({
    queryKey: ["imports", "list", supplierId ?? "", status ?? "", cursor ?? ""], queryFn: () => importJobs.list({ supplierId, status: status ? [status] : undefined }, { limit: PAGE, cursor, count: true }),
    enabled: readable, placeholderData: keepPreviousData, refetchInterval: (q) => (q.state.data?.data.items.some((j) => running(j.status)) ? POLL_MS * 5 : false),
  });
  if (!readable) return <Screen id="s-price-imports"><PageHead title="Price-list imports" /><NoAccessPanel page="Price-list imports" permission="imports.job.read" /></Screen>;
  const set = (k: string, v: string | undefined) => { const next = new URLSearchParams(sp); if (v) next.set(k, v); else next.delete(k); setSp(next, { replace: true }); };
  const items = list.data?.data.items ?? [];
  const total = list.data?.data.totalCount;
  const first = cursors.length * PAGE + 1;
  return (
    <Screen id="s-price-imports">
      <PageHead title="Price-list imports" sub={<>Stock{DOT}lens labs' price lists, newest first{DOT}nothing changes until an import is applied</>}
        actions={<>
          <Link className="btn" to="/products">Products</Link>
          {access.write && <Link className="btn primary" to="/price-import/new"><Icon name="plus" />New import</Link>}
        </>} />
      {access.read && (
        <div className="toolbar">
          <Select aria-label="Supplier" boxClass="w-sel" value={supplierId ?? ""} onChange={(e) => set("supplier", e.target.value || undefined)}>
            <option value="">All lens labs</option>
            {(labs.data?.data.items ?? []).filter((l) => l.types.includes("LensLab")).map((l) => <option key={l.id} value={l.id}>{l.name}{l.status === "Inactive" ? " (inactive)" : ""}</option>)}
          </Select>
          <Select aria-label="Status" boxClass="w-sel" value={status ?? ""} onChange={(e) => set("status", e.target.value || undefined)}>
            <option value="">Any status</option>
            {IMPORT_JOB_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </Select>
          {total !== undefined && <span className="right small faint">{formatCount(total)} import{total === 1 ? "" : "s"}</span>}
        </div>
      )}
      {list.isPending && <Loading what="the imports" />}
      {list.isError && (isApiError(list.error) && list.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Price-list imports" error={list.error} /> : <ErrorAlert error={list.error} />)}
      {list.data && (
        <Panel tight foot={(cursors.length > 0 || list.data.data.nextCursor) ? <Pager label={`${formatCount(first)}–${formatCount(first + items.length - 1)}${total !== undefined ? ` of ${formatCount(total)}` : ""}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor}
          onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => { const n = list.data?.data.nextCursor; if (n) setCursors((c) => [...c, n]); }} /> : undefined}>
          {items.length === 0 ? (
            !access.read
              ? <Empty title="No import you may open.">Price-list imports are an Owner's or an Admin's: opening one needs pricing.price_list.write.</Empty>
              : <Empty title={supplierId || status ? "No import matches these filters." : "No price list has been imported yet."} actions={access.write && !supplierId && !status ? <Link className="btn primary" to="/price-import/new">New import</Link> : undefined}>
                  {supplierId || status ? undefined : "A lens lab's lenses are priced from its list once the list is imported."}
                </Empty>
          ) : (
            <div className="tbl-scroll">
              <table>
                <thead><tr><th>Uploaded</th><th>Supplier · file</th><th>List</th><th>Status</th><th className="r">Lines</th><th>Result</th></tr></thead>
                <tbody>
                  {items.map((j) => (
                    <tr key={j.id} className="clickable" onClick={() => navigate(`/price-import/${j.id}`)}>
                      <td className="nw">{formatDate(j.uploadedAt)}<div className="small faint">{formatTime(j.uploadedAt)}{j.uploadedBy ? ` · ${shortName(j.uploadedBy.displayName)}` : ""}</div></td>
                      <td className="supplier"><Link to={`/price-import/${j.id}`} onClick={(e) => e.stopPropagation()}>{j.supplier?.name ?? "—"}</Link><div className="small faint mono file">{j.file.name}</div></td>
                      <td className="nw">{listDatedWords(j)}{scopeWords(j, oneStore) !== null && <div className="small faint">{scopeWords(j, oneStore)}</div>}</td>
                      <td><ImportStatusChip job={j} /><div className="small faint nw">{statusWhen(j)}</div>{j.appliedBy && j.appliedBy.id !== j.uploadedBy?.id && <div className="small faint nw">applied by {shortName(j.appliedBy.displayName)}</div>}</td>
                      <td className="r num nw">{formatCount(j.lines)}</td>
                      <td className="small">{resultWords(j)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </Screen>
  );
}

/** When an import reached its status: applied, discarded, or the phase it stopped in. */
function statusWhen(j: ImportJobView): string {
  if (j.status === "Imported") return `${formatDate(j.completedAt ?? j.appliedAt)} ${formatTime(j.completedAt ?? j.appliedAt)}`;
  if (j.status === "Discarded") return `${formatDate(j.discardedAt)} ${formatTime(j.discardedAt)}`;
  if (j.status === "Failed") return checkStopped(j) ? "during its check" : "during its Apply";
  return "";
}

/** The history's Result column (BR-IMPORTS-018). */
function resultWords(j: ImportJobView): string {
  if (j.outcome) return outcomeWords(j.outcome);
  if (j.progress) return `${j.progress.phase === "Check" ? "checking" : "writing"} — ${formatCount(j.progress.done)} of ${formatCount(j.progress.total)} lines`;
  if (j.status === "Discarded") return "nothing applied";
  if (j.status === "Uploaded") return "not checked yet";
  if (j.counts) return `${formatCount(j.counts.toReview)} to review · ${formatCount(j.counts.ticked)} ticked`;
  return "";
}

// --- /price-import/new: step 1, Supplier & file (UC-IMPORTS-001) --------------------------------------------------------------

/** What the screen says for each refusal of the file — before it is sent (E1, E2) and as the server answers it. */
const FILE_WORDS: Readonly<Record<string, string>> = {
  too_large: "The file is larger than 5 MB.",
  workbook: "Excel workbooks are read in a later sprint — save the sheet as CSV UTF-8 and upload that.",
  type_not_allowed: "Excel workbooks are read in a later sprint — save the sheet as CSV UTF-8 and upload that.",
  binary: "This is not a CSV file — save the sheet as CSV UTF-8 and upload that.",
  not_utf8: "The file is not UTF-8 text — save it as CSV UTF-8 and upload it again.",
  empty: "The file is empty.",
  no_rows: "The file has a header but no price line under it.",
  too_many_rows: `The file holds more than ${formatCount(IMPORT_LIMITS.priceLinesMax)} price lines.`,
  required: "Choose the supplier's CSV file.",
};

/** Newest first, as pricing orders a scope's lists: the later date, then the later-made list. */
const newestFirst = (a: SupplierPriceListView, b: SupplierPriceListView): number =>
  a.listDate !== b.listDate ? (a.listDate < b.listDate ? 1 : -1) : a.createdAt !== b.createdAt ? (a.createdAt < b.createdAt ? 1 : -1) : (a.id < b.id ? 1 : -1);

/**
 * The supplier's lists as the server reads them for an upload (BR-PRICING-019, BR-IMPORTS-023; UC-IMPORTS-001 step 2, A2): the
 * scope's own Current lists, whatever their kind, newest first — every one of which Apply supersedes; the one the new list
 * follows — of its own kind (`Lens`), else the newest — whose name it keeps; and the list its terms come from: that one, or, for
 * a branch with no list of its own, the organisation's by the same rule.
 */
function scopeListsFor(lists: readonly SupplierPriceListView[], scope: "AllBranches" | "Branch", branchId: string | null) {
  const of = (b: string | null) => lists.filter((l) => l.status === "Current" && l.withdrawnAt === null && l.branchId === b).sort(newestFirst);
  const follows = (ls: readonly SupplierPriceListView[]) => ls.find((l) => l.kind === "Lens") ?? ls[0] ?? null;
  const own = of(scope === "AllBranches" ? null : branchId);
  const baseline = follows(own);
  return { own, baseline, termsFrom: baseline ?? (scope === "Branch" ? follows(of(null)) : null) };
}

/**
 * The scope's current lists under the terms (UC-IMPORTS-001 step 2, A2): the one list, or every list Apply will supersede and the
 * one whose terms the new list takes; or, for a branch with none of its own, the organisation's list its terms come from.
 */
function CurrentLists({ own, baseline, termsFrom, branch, supplier }: {
  own: readonly SupplierPriceListView[]; baseline: SupplierPriceListView | null; termsFrom: SupplierPriceListView | null; branch: string; supplier: string;
}) {
  const kindOf = (l: SupplierPriceListView) => (l.kind === "Lens" ? null : LIST_KIND_WORDS[l.kind]);
  if (own.length === 1) {
    const l = own[0]!;
    return <div className="small faint">Current list: {[l.name, kindOf(l), `dated ${dayText(l.listDate)}`, `${formatCount(l.lineCount)} lines`, l.lensMarkupBp !== null ? `+${percentFromBp(l.lensMarkupBp)}` : null, l.branch ? `${l.branch.name} only` : null, "it stays current until the new list is applied"].filter(Boolean).join(DOT)}</div>;
  }
  if (own.length > 1) {
    return <div className="small faint">Current lists: {own.map((l) => [l.name, kindOf(l), `dated ${dayText(l.listDate)}`, `${formatCount(l.lineCount)} lines`].filter(Boolean).join(DOT)).join("; ")}{DOT}the new list takes {possessive(baseline!.name)} terms; they stay current until it is applied, which supersedes them all</div>;
  }
  if (termsFrom) {
    return <div className="small faint">{branch} has no list of its own from {supplier}: its terms are the organisation's list's — {[termsFrom.name, termsFrom.lensMarkupBp !== null ? `+${percentFromBp(termsFrom.lensMarkupBp)}` : null].filter(Boolean).join(DOT)} — which stays current</div>;
  }
  return null;
}

interface Chosen { file: File; bytes: Uint8Array }

/**
 * UC-IMPORTS-001 steps 1–3, A1, A2, A6, E1–E9: the upload, its terms defaulted from the current list, each refusal under its field.
 * At a practice of one store (A9; change 13) the list is the organisation's, which its one branch takes: no choice of scope and no
 * word of where it applies, an address asking for a branch's own list included.
 */
export function PriceImportNew() {
  const s = useSessionView();
  const access = useImportAccess();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const acting = useActingBranch();
  const block = useWriteBlock();
  const branches = s?.switchableBranches ?? [];
  const [supplierId, setSupplierId] = useState(sp.get("supplierId") ?? "");
  const [allBranchesTicked, setAllBranches] = useState(sp.get("scope") !== "Branch");
  const oneStore = useOneStore();
  const allBranches = oneStore || allBranchesTicked;
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [fileLocal, setFileLocal] = useState<string | null>(null);
  const [listDate, setListDate] = useState("");
  const [markup, setMarkup] = useState<string | null>(null);
  const [discount, setDiscount] = useState<string | null>(null);
  const [listName, setListName] = useState("");
  const [local, setLocal] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const labs = useQuery({ queryKey: ["suppliers", "lens-labs", "Active"], queryFn: () => suppliers.list({ type: "LensLab", status: "Active" }, { limit: 200 }), enabled: access.write });
  const lists = useQuery({ queryKey: ["pricing", "price-lists", supplierId, "Current"], queryFn: () => pricing.priceLists({ supplierId, status: ["Current"] }), enabled: access.write && supplierId !== "" });
  const scope = allBranches ? "AllBranches" as const : "Branch" as const;
  const { own, baseline, termsFrom } = lists.data ? scopeListsFor(lists.data.data.items, scope, acting.id) : { own: [], baseline: null, termsFrom: null };
  // Whose terms the defaults are: the scope's own list's, or — a branch with none — the organisation's.
  const termsWhose = termsFrom && termsFrom === baseline ? "the current list's" : "the organisation's list's";
  const defaultMarkup = termsFrom?.lensMarkupBp ?? null;
  const defaultDiscount = termsFrom?.discountBp ?? null;
  // A changed supplier or scope brings its own defaults back (BR-IMPORTS-023).
  useEffect(() => { setMarkup(null); setDiscount(null); }, [supplierId, scope]);
  const key = useMemo(() => newIdempotencyKey(), [supplierId, scope, chosen, listDate, markup, discount, listName]);
  const need = missing(s, ["imports.job.read", "imports.job.write", "pricing.price_list.write"]);
  if (need) return <Screen id="s-price-import"><PageHead title="Import price list" /><NoAccessPanel page="the price-list import" permission={need} /></Screen>;

  const labRows: SupplierListItem[] = (labs.data?.data.items ?? []).filter((l) => l.types.includes("LensLab") && l.status === "Active");
  const supplierName = labRows.find((l) => l.id === supplierId)?.name ?? "The supplier";
  const markupText = markup ?? percentTextFromBp(defaultMarkup);
  const discountText = discount ?? percentTextFromBp(defaultDiscount);
  const defaultName = baseline?.name ?? (chosen ? chosen.file.name.replace(/\.csv$/i, "") : "");

  const choose = async (f: File | null) => {
    setError(null);
    setLocal((l) => { const { file: _f, ...rest } = l; return rest; });
    if (!f) { setChosen(null); setFileLocal(null); return; }
    const head = await bytesOf(f.slice(0, 8));
    const refusal = refusalOf(f.name, f.size, head);
    if (refusal) { setChosen(null); setFileLocal(FILE_WORDS[refusal] ?? null); return; }
    setFileLocal(null);
    setChosen({ file: f, bytes: await bytesOf(f) });
  };

  const submit = async () => {
    if (busy) return;
    setError(null);
    const found: Record<string, string> = {};
    if (!supplierId) found.supplierId = "Choose the lens lab whose list this is.";
    if (!chosen) found.file = fileLocal ?? FILE_WORDS.required!;
    if (!listDate) found.listDate = "Enter the date the supplier gives the list.";
    const markupBp = markupText.trim() === "" ? null : bpFromPercentText(markupText);
    if (markupText.trim() === "" && defaultMarkup === null && supplierId) found.lensMarkupBp = "A supplier's first list needs its lens markup.";
    else if (markupText.trim() !== "" && markupBp === null) found.lensMarkupBp = "Enter a percentage such as 90.";
    const discountBp = discountText.trim() === "" ? null : bpFromPercentText(discountText);
    if (discountText.trim() !== "" && discountBp === null) found.discountBp = "Enter a percentage such as 5, or leave it empty.";
    setLocal(found);
    if (Object.keys(found).length > 0 || !chosen) return;
    const body: UploadBody = {
      kind: "PriceList", supplierId, scope, listDate, file: { fileName: chosen.file.name, data: base64Of(chosen.bytes) },
      ...(listName.trim() ? { listName: listName.trim() } : {}),
      ...(markupBp !== null && markupBp !== defaultMarkup ? { lensMarkupBp: markupBp } : {}),
      ...(discountBp !== defaultDiscount ? { discountBp } : {}),
    };
    setBusy(true);
    try {
      const res = await importJobs.upload(body, key);
      const v = res.data;
      keepFileText(v.id, textOf(chosen.bytes), await sha256Of(chosen.bytes));
      qc.setQueryData(["import", v.id], res as ApiResult<ImportJobView>);
      void qc.invalidateQueries({ queryKey: ["imports"] });
      navigate(`/price-import/${v.id}`, { state: { warnings: v.warnings } satisfies ImportPageState });
    } catch (err) {
      // Uploaded again under its key after a lost answer and answered as carried out, its answer not kept (BR-GOVERNANCE-016): the
      // import exists, its id gone with the answer. It is found among the supplier's uploaded imports — the newest of this scope
      // whose file is this one, by name, size and SHA-256 — and opened at its mapping; not found, the page says where to look
      // (UC-IMPORTS-001 A6 step 2).
      if (carriedOut(err)) {
        void qc.invalidateQueries({ queryKey: ["imports"] });
        const sha = await sha256Of(chosen.bytes);
        const uploaded = await importJobs.list({ supplierId, status: ["Uploaded"] }).then((r) => r.data.items.find((j) => j.scope === scope && (scope === "AllBranches" || j.branch?.id === acting.id) && j.file.name === chosen.file.name && j.file.sizeBytes === chosen.file.size && (sha === null || j.file.sha256 === sha)) ?? null, () => null);
        if (uploaded) {
          keepFileText(uploaded.id, textOf(chosen.bytes), sha);
          navigate(`/price-import/${uploaded.id}`);
          return;
        }
      }
      setError(err);
    }
    finally { setBusy(false); }
  };

  const api = isApiError(error) ? error : null;
  const fieldOf = (path: string): string | undefined => {
    if (local[path]) return local[path];
    const f = api?.field(path);
    if (!f) return undefined;
    if (path === "file") return FILE_WORDS[f.code] ?? f.message;
    if (path === "supplierId") return f.code === "inactive" ? `${supplierName} is inactive.` : f.code === "not_lens_lab" ? `${supplierName} is not a lens lab — Sprint 4 imports lens labs' lists only.` : f.message;
    // The server refuses a date before the newest of the scope's lists, whatever its kind (BR-PRICING-019): that one is named.
    if (path === "listDate") return f.code === "in_future" ? "Choose a date no later than today." : f.code === "older_than_current" ? `The list is older than the current list${own[0] ? `, dated ${dayText(own[0].listDate)}` : ""}.` : f.code === "required" ? "Enter the date the supplier gives the list." : f.message;
    if (path === "listName") return f.code === "too_long" ? `At most ${IMPORT_LIMITS.listNameMax} characters.` : f.message;
    if (path === "lensMarkupBp") return f.code === "required" ? "A supplier's first list needs its lens markup." : f.code === "out_of_range" ? "Between 0% and 1 000%." : f.message;
    if (path === "discountBp") return f.code === "out_of_range" ? "Between 0% and 100%." : f.message;
    return f.message;
  };
  const shown = new Set(["file", "supplierId", "listDate", "listName", "lensMarkupBp", "discountBp"]);
  const general = error !== null && !(api && api.errors.length > 0 && api.errors.every((e) => shown.has(e.path)));
  const otherBranches = branches.map((b) => b.name);
  return (
    <Screen id="s-price-import">
      <PageHead title="Import price list" sub={<>Step 1 of 4{DOT}a lens lab's list as a CSV file{DOT}nothing changes until Apply</>}
        actions={<Link className="btn" to="/price-import">Price-list imports</Link>} />
      <Stepper current={0} />
      <form className="panel" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        <div className="panel-head"><h2>Supplier &amp; file</h2><span className="meta">the list stays as it is until the import is applied</span></div>
        <div className="panel-body stack" style={{ gap: 14 }}>
          {general && (carriedOut(error)
            ? <Alert tone="ok" role="status"><b>The file was uploaded: its answer did not arrive.</b> Open it under <Link to="/price-import">Price-list imports</Link> — uploading it again will not make a second import.</Alert>
            : <Alert tone="crit" role="alert"><b>Not uploaded.</b> {api?.code === "NOT_FOUND" ? "The supplier or the branch was not found — it may have been deleted." : describeError(error)}</Alert>)}
          <div className="grid-2">
            <Field label="Supplier" required htmlFor="pi-supplier" error={fieldOf("supplierId")} help="An active lens lab">
              <Select id="pi-supplier" value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setLocal((l) => { const { supplierId: _s, ...r } = l; return r; }); setError(null); }}>
                <option value="">Choose a lens lab…</option>
                {labRows.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </Field>
            <Field label="Price list file" required htmlFor="pi-file" error={fieldOf("file")} help={chosen ? <><span className="mono">{chosen.file.name}</span>{DOT}{formatFileSize(chosen.file.size)}</> : "A CSV of at most 5 MB and 20 000 lines; an Excel sheet is saved as CSV UTF-8 first"}>
              <TextInput id="pi-file" type="file" accept=".csv,text/csv" onChange={(e) => void choose(e.target.files?.[0] ?? null)} />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="List dated" required htmlFor="pi-date" error={fieldOf("listDate")} help="The supplier's date for the list — its costs apply from it">
              <TextInput id="pi-date" type="date" mono max={todayLocal()} value={listDate} onChange={(e) => { setListDate(e.target.value); setLocal((l) => { const { listDate: _d, ...r } = l; return r; }); }} />
            </Field>
            <Field label="List name" htmlFor="pi-name" error={fieldOf("listName")} help={defaultName ? `Empty keeps “${defaultName}”` : "Empty keeps the current list's name, or the file's"}>
              <TextInput id="pi-name" value={listName} maxLength={IMPORT_LIMITS.listNameMax + 20} placeholder={defaultName} onChange={(e) => setListName(e.target.value)} />
            </Field>
          </div>
          {!oneStore && (
            <div className="stack" style={{ gap: 6 }}>
              <Checkbox checked={allBranches} onChange={setAllBranches}>Applies to all branches{otherBranches.length > 0 ? ` — ${otherBranches.join(", ")}` : ""}</Checkbox>
              <span className="help">{allBranches ? "The supplier's organisation-wide list; each branch keeps its own price overrides." : `${acting.name}'s own list — it overrides the organisation's line by line at ${acting.name} only.`}</span>
            </div>
          )}
          <div className="grid-2">
            <Field label="Lens markup" required={defaultMarkup === null} htmlFor="pi-markup" error={fieldOf("lensMarkupBp")}
              help={defaultMarkup !== null ? `% on the lab price; ${termsWhose} is +${percentFromBp(defaultMarkup)}` : "% on the lab price — this supplier has no list yet, so give it"}>
              <TextInput id="pi-markup" mono inputMode="decimal" value={markupText} onChange={(e) => setMarkup(e.target.value)} />
            </Field>
            <Field label="Discount" htmlFor="pi-discount" error={fieldOf("discountBp")} help={defaultDiscount !== null ? `% off the lab price; ${termsWhose} is ${percentFromBp(defaultDiscount)}` : "% off the lab price; empty is the supplier's default"}>
              <TextInput id="pi-discount" mono inputMode="decimal" value={discountText} onChange={(e) => setDiscount(e.target.value)} />
            </Field>
          </div>
          <CurrentLists own={own} baseline={baseline} termsFrom={termsFrom} branch={acting.name} supplier={supplierName} />
        </div>
        <div className="panel-foot">
          <Link className="btn" to="/price-import">Cancel</Link>
          <span className="help">The file is read and checked against the supplier's current list before anything changes.</span>
          <span className="right" />
          <button className="btn primary" type="submit" disabled={busy || block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined}>{busy ? "Uploading…" : "Upload and continue"}</button>
        </div>
      </form>
    </Screen>
  );
}

// --- /price-import/{id}: one import at its step --------------------------------------------------------------------------------

/** What step 1 hands the import's page: the upload's warnings (UC-IMPORTS-001 A5). */
export interface ImportPageState { warnings?: ImportWarning[] | undefined }

const STEPS = ["Supplier & file", "Mapping", "Review", "Apply"] as const;

/** `price-import`'s stepper: the steps done, the current one, and the mapping in words once the file is mapped. */
export function Stepper({ current, done, mapped }: { current: number; done?: boolean | undefined; mapped?: string | undefined }) {
  return (
    <div className="panel">
      <ol className="steps" aria-label="Steps">
        {STEPS.map((label, i) => {
          const state = done || i < current ? "done" : i === current ? "cur" : "";
          return (
            <li key={label} className={`st${state ? ` ${state}` : ""}`} aria-current={state === "cur" ? "step" : undefined}>
              {i > 0 && <span className="sep" aria-hidden="true" />}
              <span className="n">{state === "done" ? <Icon name="check" /> : i + 1}</span>{label}
            </li>
          );
        })}
        {mapped && <li className="right small faint mapped">Mapped: {mapped}</li>}
      </ol>
    </div>
  );
}

/** UC-IMPORTS-005 step 3, A3: one import, opened at its step; read again every two seconds while its check or Apply runs. */
export function PriceImportPage() {
  const s = useSessionView();
  const { id = "" } = useParams();
  const location = useLocation();
  const [sp] = useSearchParams();
  const need = missing(s, ["imports.job.read", "pricing.price_list.write"]);
  const job = useQuery({
    queryKey: ["import", id], queryFn: () => importJobs.get(id), enabled: !need,
    refetchInterval: (q) => (running(q.state.data?.data.status) ? POLL_MS : false),
  });
  const passed = (location.state ?? null) as ImportPageState | null;
  if (need) return <Screen id="s-price-import"><PageHead title="Import price list" /><NoAccessPanel page="the price-list import" permission={need} /></Screen>;
  if (job.isPending) return <Screen id="s-price-import"><Loading what="the import" /></Screen>;
  // A read that fails once the import has been read — a refetch after a refused write, one poll of a running check — keeps the
  // step as last read, with a notice, so nothing typed on it is lost (UC-IMPORTS-005 A6). Only a first read that fails, or an
  // import that is gone or no longer the user's to open, shows the page's refusal instead.
  const gone = job.isError && isApiError(job.error) && (job.error.code === "NOT_FOUND" || job.error.code === "PERMISSION_DENIED");
  if (job.isError && (gone || !job.data)) {
    return (
      <Screen id="s-price-import">
        <PageHead title="Import price list" actions={<Link className="btn" to="/price-import">Price-list imports</Link>} />
        {isApiError(job.error) && job.error.code === "NOT_FOUND" ? <Alert tone="warn"><b>This import was not found.</b> An import discarded before it was applied is removed; it may also belong to another organisation.</Alert>
          : isApiError(job.error) && job.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="the price-list import" error={job.error} /> : <ErrorAlert error={job.error} />}
      </Screen>
    );
  }
  const v = job.data!.data;
  const failedCheck = checkStopped(v);
  const view = v.status === "Uploaded" || ((v.status === "Checked" || failedCheck) && sp.get("step") === "mapping") ? "mapping"
    : v.status === "Checked" ? "review" : v.status === "Checking" ? "checking" : failedCheck ? "check-stopped"
    : v.status === "Importing" ? "applying" : v.status === "Failed" ? "import-stopped" : v.status === "Imported" ? "result" : "discarded";
  const current = view === "mapping" ? 1 : view === "review" || view === "checking" || view === "check-stopped" ? 2 : 3;
  return (
    <Screen id="s-price-import">
      <ImportHead v={v} step={view === "result" || view === "discarded" ? null : current + 1} />
      <Stepper current={current} done={view === "result"} mapped={current >= 2 && v.mapping ? mappedSummary(v.mapping, v.header) : undefined} />
      {job.isError && (
        <Alert tone="warn" role="alert" actions={<button className="btn sm" type="button" disabled={job.isFetching} onClick={() => void job.refetch()}>{job.isFetching ? "Reading…" : "Try again"}</button>}>
          <b>The import could not be read again.</b> The page shows it as it was last read; nothing you entered is lost.{running(v.status) ? " It is read again every two seconds while it runs." : ""} {describeError(job.error)}
        </Alert>
      )}
      {view === "mapping" && <MappingStep job={v} warnings={passed?.warnings ?? []} />}
      {view === "review" && <ReviewStep job={v} />}
      {(view === "checking" || view === "applying") && <RunProgress job={v} />}
      {view === "check-stopped" && <CheckStopped job={v} />}
      {view === "import-stopped" && <ImportStopped job={v} />}
      {view === "result" && <ImportResult job={v} />}
      {view === "discarded" && <DiscardedPanel job={v} />}
    </Screen>
  );
}

/** The page head: whose list, the file, its date, its layout and the step; Cancel import while it may be discarded. */
function ImportHead({ v, step }: { v: ImportJobView; step: number | null }) {
  const [discarding, setDiscarding] = useState(false);
  const block = useWriteBlock();
  const access = useImportAccess();
  const layout = v.layout ? `format recognised: ${v.layout.name}` : v.mapping ? "mapped by hand" : "not mapped yet";
  const tail = step !== null ? `step ${step} of 4 — ${step === 4 ? "nothing becomes current until the last step" : "nothing changes until Apply"}`
    : v.status === "Imported" ? `applied ${formatDate(v.completedAt ?? v.appliedAt)} ${formatTime(v.completedAt ?? v.appliedAt)}${v.appliedBy ? ` by ${shortName(v.appliedBy.displayName)}` : ""}`
    : `discarded ${formatDate(v.discardedAt)}${v.discardedBy ? ` by ${shortName(v.discardedBy.displayName)}` : ""}`;
  return (
    <>
      <PageHead title={`Import price list — ${v.supplier?.name ?? "supplier"}`}
        sub={<><span className="mono">{v.file.name}</span>{DOT}{listDatedWords(v)}{DOT}{layout}{DOT}{tail}</>}
        actions={<>
          <Link className="btn" to="/price-import">Price-list imports</Link>
          {access.write && v.moves.includes("discard") && <button className="btn" type="button" disabled={block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setDiscarding(true)}>Cancel import</button>}
        </>} />
      {discarding && <DiscardDialog v={v} onClose={() => setDiscarding(false)} />}
    </>
  );
}

/** UC-IMPORTS-006 steps 2–5, A1, E1, E2, E6: asks first; an import whose Apply never started goes, one whose Apply started stays Discarded. */
function DiscardDialog({ v, onClose }: { v: ImportJobView; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const started = v.commit !== null || v.appliedAt !== null;
  const discard = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await importJobs.discard(v.id);
      void qc.invalidateQueries({ queryKey: ["imports"] });
      if (started) { await qc.invalidateQueries({ queryKey: ["import", v.id] }); onClose(); return; }
      qc.removeQueries({ queryKey: ["import", v.id] });
      // UC-IMPORTS-006 step 5, A5: every current list of the scope stays current, named as the Review names them; none, nothing changed.
      navigate("/settings/markups", { state: { notice: `The import of ${v.file.name} was discarded; ${v.supersedes.length > 1 ? `${andList(v.supersedes.map((l) => l.name))} stay current` : v.currentList ? `${v.currentList.name} stays current` : "nothing was changed"}.` } });
    } catch (err) {
      setError(err);
      // Refused — a run started meanwhile, another user discarded it — the import is read again, so the page behind shows its step.
      void qc.invalidateQueries({ queryKey: ["import", v.id], exact: true });
    } finally { setBusy(false); }
  };
  const api = isApiError(error) ? error : null;
  const words = api?.code === "ILLEGAL_TRANSITION"
    ? api.meta.reason === "running" ? "The import is running; wait until its check or Apply ends." : api.meta.reason === "imported" ? "The import has been applied; an applied import is undone by the rollback of Sprint 11." : `The import is ${String(api.meta.from ?? "no longer discardable")}; read it again.`
    : error !== null ? describeError(error) : null;
  return (
    <Modal title="Discard this import?" meta={v.file.name} onClose={onClose} onSubmit={() => void discard()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Keep</button>
      <button className="btn danger" type="submit" disabled={busy}>{busy ? "Discarding…" : "Discard"}</button>
    </>}>
      {words && <Alert tone="crit" role="alert"><b>Not discarded.</b> {words}</Alert>}
      <p>{started
        ? "Its draft list is withdrawn and the lens lines it added are voided; nothing became current. The import stays under Price-list imports as Discarded."
        : "The file and your ticks are removed; nothing was changed."}</p>
    </Modal>
  );
}
